import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorCode } from '../api/client';
import { toast } from '../components/ui/Toast';
import { preparePhoto, UnreadablePhotoError, type PreparedPhoto } from '../utils/preparePhoto';
import { tooLargeMessage, uploadErrorMessage } from '../utils/uploadLimits';

export interface UploadProgress {
  total: number;
  /** Photos already finished, stored or not. */
  done: number;
  /** How far the one in flight is, 0–1. */
  current: number;
}

type Send<T> = (photo: PreparedPhoto, onProgress: (pct: number) => void) => Promise<T>;

/**
 * Shrinks and uploads photos one at a time, with progress. One at a time on
 * purpose: the server decodes one photo at a time anyway, and a phone holding
 * 150 shrunk copies at once would run out of memory itself.
 */
export function usePhotoUploads() {
  const { t } = useTranslation();
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const uploading = progress !== null;

  // Closing the tab mid-batch silently drops the rest.
  useEffect(() => {
    if (!uploading) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [uploading]);

  async function uploadAll<T>(files: File[], send: Send<T>): Promise<T[]> {
    const total = files.length;
    const stored: T[] = [];
    setProgress({ total, done: 0, current: 0 });
    try {
      for (const [done, original] of files.entries()) {
        setProgress({ total, done, current: 0 });
        const photo = await preparePhoto(original).catch((e: unknown) => {
          if (e instanceof UnreadablePhotoError) return null;
          throw e;
        });
        if (!photo) { toast(t('upload.imageUnreadable', { name: original.name }), 'error'); continue; }
        const tooLarge = tooLargeMessage(photo.file, 'photo');
        if (tooLarge) { toast(tooLarge, 'error'); continue; }
        try {
          stored.push(await send(photo, pct => setProgress({ total, done, current: pct / 100 })));
        } catch (e) {
          // The rest would be refused one by one: say it once and stop.
          if (apiErrorCode(e) === 'STORAGE_QUOTA_EXCEEDED') {
            toast(t('upload.storageFullBatch', { count: total - done }), 'error');
            break;
          }
          toast(uploadErrorMessage(e, original.name, 'photo', t('gallery.uploadError', { name: original.name })), 'error');
        }
      }
    } finally {
      setProgress(null);
    }
    return stored;
  }

  return { progress, uploading, uploadAll };
}
